import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { DialogActions, Grid, Typography, Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import EMPButton from "../../EMPButton/EMPButton";
import EMPCheckbox from "../../EMPCheckbox/EMPCheckbox";
import { ModalContext } from "../ModalContext";
import {
  doesAttestationExist,
  setAttestation,
} from "../../../services/localstorage/LocalStorageService";
import {
  trackTimeOfSaleReportPopupAction,
  trackTimeOfSaleReportPopupLoad,
} from "../../../services/analytics/OrderDetailsPageAnalytics";
import { UserContext } from "../../../context/userContext";
import { trackReferralExit } from "../../../services/analytics/AnalyticsService";

const TIME_OF_SALE_ATTESTATION_KEY = "time-of-sale";
const IRS_LINK = "https://www.irs.gov";

const TimeOfSaleReport = ({ orderId }) => {
  const { handleModal } = useContext(ModalContext);
  const [checked, setChecked] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);

  useEffect(() => {
    trackTimeOfSaleReportPopupLoad(orderId, user);
  }, [orderId, user]);

  useEffect(() => {
    const existingAttestation = doesAttestationExist(
      orderId,
      TIME_OF_SALE_ATTESTATION_KEY
    );

    if (existingAttestation) {
      setChecked(true);
      setDisabled(true);
    }
  }, [orderId]);

  const handleChange = (checkedBool) => {
    if (doesAttestationExist(orderId, TIME_OF_SALE_ATTESTATION_KEY)) {
      return;
    }

    setChecked(checkedBool);
    setDisabled(!checkedBool);
  };

  const handleReferralClick = () => {
    trackReferralExit(IRS_LINK, orderId, user);
  };

  const handleCancel = () => {
    handleModal(false, false);
    trackTimeOfSaleReportPopupAction("cancel", orderId, user);
  };

  const saveAttestation = () => {
    setAttestation(TIME_OF_SALE_ATTESTATION_KEY, orderId, new Date());
    handleModal(false, false);
    queryClient.invalidateQueries("order", { orderId });
    trackTimeOfSaleReportPopupAction("confirm completed", orderId, user);
  };

  return (
    <div>
      <Typography variant="body1" sx={{ mb: 6 }}>
        You must submit a time of sale report on the IRS website:{" "}
        <Link
          to={{
            pathname: IRS_LINK,
          }}
          target="_blank"
          referrer="noreferrer"
          onClick={handleReferralClick}
        >
          {IRS_LINK}
        </Link>
      </Typography>

      <Box sx={{ mb: 10 }}>
        <EMPCheckbox
          label="I attest that I have completed this report and submitted it to the IRS."
          checked={checked}
          handleChange={handleChange}
        />
      </Box>

      <DialogActions sx={{ padding: "16px 0" }}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <EMPButton variant="text" onClick={handleCancel}>
              Cancel
            </EMPButton>
          </Grid>

          <Grid item>
            <EMPButton
              variant="contained"
              onClick={saveAttestation}
              disabled={disabled}
            >
              Confirm Completed
            </EMPButton>
          </Grid>
        </Grid>
      </DialogActions>
    </div>
  );
};

TimeOfSaleReport.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default TimeOfSaleReport;
